import { useState, useEffect } from "react";
import { Link, useLocation, useNavigate } from "react-router-dom";
import { BarChart3, Settings, Grid3x3, LogOut, Users, Network, UserPlus, Calculator, Zap, Sun, Moon, HelpCircle, ChevronLeft, ChevronRight, BookUser, GitBranch, Menu, User, Globe, ClipboardList } from "lucide-react";
import { cn } from "@/lib/utils";
import { useAuth } from "@/hooks/useAuth";
import { usePermissions } from "@/hooks/usePermissions";
import { Button } from "./ui/button";
import { toast } from "sonner";
import { Tooltip, TooltipContent, TooltipTrigger } from "./ui/tooltip";
import { Sheet, SheetContent, SheetHeader, SheetTitle, SheetTrigger } from "./ui/sheet";
import VersionDisplay from "./VersionDisplay";
import { useIsMobile } from "@/hooks/use-mobile";

// Icon-Map für dynamisches Rendering
const iconMap: Record<string, React.ComponentType<{
  className?: string;
}>> = {
  BarChart3,
  Settings,
  Grid3x3,
  Users,
  Network,
  UserPlus,
  Calculator,
  HelpCircle,
  BookUser,
  GitBranch,
  User
};
const Sidebar = () => {
  const location = useLocation();
  const navigate = useNavigate();
  const {
    profile,
    userRole,
    signOut
  } = useAuth();
  const {
    canRead,
    loading: permissionsLoading
  } = usePermissions();
  const isMobile = useIsMobile();
  const [isOpen, setIsOpen] = useState(false);
  const [isCollapsed, setIsCollapsed] = useState(() => {
    const stored = localStorage.getItem("hr-flash-sidebar-collapsed");
    return stored === "true";
  });
  const [isDarkMode, setIsDarkMode] = useState(() => {
    const stored = localStorage.getItem("hr-flash-theme");
    return stored !== "light";
  });
  useEffect(() => {
    localStorage.setItem("hr-flash-sidebar-collapsed", String(isCollapsed));
  }, [isCollapsed]);
  useEffect(() => {
    const root = document.documentElement;
    if (isDarkMode) {
      root.classList.add("dark");
      root.classList.remove("light");
      localStorage.setItem("hr-flash-theme", "dark");
    } else {
      root.classList.remove("dark");
      root.classList.add("light");
      localStorage.setItem("hr-flash-theme", "light");
    }
  }, [isDarkMode]);
  const toggleTheme = () => {
    setIsDarkMode(!isDarkMode);
  };
  const toggleSidebar = () => {
    setIsCollapsed(!isCollapsed);
  };
  const handleSignOut = async () => {
    await signOut();
    toast.success("Erfolgreich abgemeldet");
    navigate("/auth", {
      replace: true
    });
  };
  const isEmployee = userRole === 'employee';
  const isHrClerk = userRole === 'hr_clerk';
  const isManagement = userRole === 'admin' || userRole === 'hr_manager';
  const employeeId = profile?.employee_id;

  // Navigation basierend auf Rolle:
  // - Employee: Nur Baseline (Meine Daten, Kollegenverzeichnis, Organigramm, Arbeitsprozesse, Hilfe)
  // - HR-Clerk: Mitarbeiterliste, aber KEIN Dashboard, KEINE Einstellungen, KEINE Bewerber
  // - Admin/HR-Manager: Voller Zugriff

  const navItems = [
  // Dashboard - NUR für Admin und HR-Manager (nicht für hr_clerk!)
  ...(isManagement ? [{
    path: "/",
    label: "Dashboard",
    icon: BarChart3
  }] : []),
  // Mitarbeiter sehen "Meine Daten" (eigene Mitarbeiterseite)
  ...(isEmployee && employeeId ? [{
    path: `/employee/${employeeId}`,
    label: "Meine Daten",
    icon: User
  }] : []),
  // Mitarbeiterliste - für Admin, HR-Manager UND HR-Clerk (wenn masterdata lesbar)
  ...(isManagement || isHrClerk && canRead("masterdata") ? [{
    path: "/employees",
    label: "Mitarbeiter",
    icon: Users
  }] : []),
  // Kollegenverzeichnis - IMMER sichtbar für alle (gemäß Spezifikation)
  {
    path: "/directory",
    label: "Kollegenverzeichnis",
    icon: BookUser
  },
  // Organigramm - IMMER sichtbar für alle (gemäß Spezifikation)
  {
    path: "/orgchart",
    label: "Organigramm",
    icon: Network
  },
  // Bewerber - NUR für Admin/HR-Manager (nicht für hr_clerk!)
  ...(isManagement ? [{
    path: "/applicants",
    label: "Bewerber",
    icon: UserPlus
  }] : []),
  // Arbeitsprozesse - IMMER sichtbar für alle (gemäß Spezifikation)
  {
    path: "/work-processes",
    label: "Arbeitsprozesse",
    icon: GitBranch
  },
  // Mitarbeiterumfrage - IMMER sichtbar für alle
  {
    path: "/survey",
    label: "Umfrage",
    icon: ClipboardList
  },
  // Kostenübersicht / Einstandslöhne - nur wenn payroll-Leserecht
  ...(canRead("payroll") ? [{
    path: "/cost-overview",
    label: "Einstandslöhne",
    icon: Calculator
  }] : []),
  // Bewertungsmatrix - nur wenn ratings-Leserecht
  ...(canRead("ratings") ? [{
    path: "/matrix",
    label: "Bewertungsmatrix",
    icon: Grid3x3
  }] : []),
  // Einstellungen - für Admin, HR-Manager UND hr_clerk (mit eingeschränkten Tabs)
  ...(isManagement || isHrClerk ? [{
    path: "/settings",
    label: "Einstellungen",
    icon: Settings
  }] : []),
  // Hilfe - IMMER sichtbar für alle (gemäß Spezifikation)
  {
    path: "/help",
    label: "Hilfe",
    icon: HelpCircle
  }];
  const NavLink = ({
    item,
    onClick
  }: {
    item: typeof navItems[0];
    onClick?: () => void;
  }) => {
    const Icon = item.icon;
    const isActive = location.pathname === item.path || item.path !== "/" && location.pathname.startsWith(item.path);
    const linkContent = <Link to={item.path} onClick={onClick} className={cn("flex items-center gap-3 px-3 py-3 rounded-lg transition-all duration-200 min-h-[44px]", isActive ? "bg-primary text-primary-foreground font-medium shadow-md" : "text-muted-foreground hover:bg-secondary hover:text-foreground", !isMobile && isCollapsed && "justify-center px-2")}>
        <Icon className="h-5 w-5 flex-shrink-0" />
        {(isMobile || !isCollapsed) && <span className="font-medium">{item.label}</span>}
      </Link>;
    if (!isMobile && isCollapsed) {
      return <Tooltip delayDuration={0}>
          <TooltipTrigger asChild>
            {linkContent}
          </TooltipTrigger>
          <TooltipContent side="right" className="font-medium">
            {item.label}
          </TooltipContent>
        </Tooltip>;
    }
    return linkContent;
  };

  // Mobile: Sheet-based navigation
  if (isMobile) {
    return <>
        {/* Fixed mobile header with hamburger */}
        <header className="fixed top-0 left-0 right-0 h-14 bg-card border-b border-border flex items-center px-4 z-50">
          <Sheet open={isOpen} onOpenChange={setIsOpen}>
            <SheetTrigger asChild>
              <Button variant="ghost" size="icon" className="mr-3">
                <Menu className="h-6 w-6" />
              </Button>
            </SheetTrigger>
            <SheetContent side="left" className="w-72 p-0 flex flex-col max-h-screen overflow-y-auto">
              <SheetHeader className="p-4 border-b border-border">
                <div className="flex items-center gap-2">
                  <div className="flex items-center justify-center w-10 h-10 rounded-lg bg-primary text-primary-foreground flex-shrink-0">
                    <Zap className="h-6 w-6" />
                  </div>
                  <div>
                    <SheetTitle className="text-lg font-bold text-foreground leading-tight text-left">HR-flash</SheetTitle>
                    <p className="text-xs text-muted-foreground">Dashboard</p>
                  </div>
                </div>
                <VersionDisplay isCollapsed={false} />
              </SheetHeader>

              {/* User Info */}
              {profile && <div className="px-4 py-3 border-b border-border">
                  <p className="text-xs text-muted-foreground">Angemeldet als</p>
                  <p className="font-medium text-sm text-foreground truncate">{profile.full_name}</p>
                  <p className="text-xs text-primary mt-0.5">
                    {userRole === 'admin' && 'Administrator'}
                    {userRole === 'hr_manager' && 'Personalmanager'}
                    {userRole === 'hr_clerk' && 'Personalverwalter'}
                    {userRole === 'employee' && 'Mitarbeiter'}
                  </p>
                </div>}

              {/* Navigation */}
              <nav className="flex-1 overflow-y-auto p-2">
                <div className="flex flex-col gap-1">
                  {navItems.map(item => <NavLink key={item.path} item={item} onClick={() => setIsOpen(false)} />)}
                </div>
              </nav>

              {/* Footer Actions */}
              <div className="p-2 border-t border-border space-y-1">
                <Button variant="ghost" onClick={toggleTheme} className="w-full justify-start gap-3 px-3 py-3 h-auto">
                  {isDarkMode ? <Sun className="h-5 w-5" /> : <Moon className="h-5 w-5" />}
                  <span className="font-medium">{isDarkMode ? "Light Mode" : "Dark Mode"}</span>
                </Button>
                <Button variant="ghost" onClick={() => {
                handleSignOut();
                setIsOpen(false);
              }} className="w-full justify-start gap-3 px-3 py-3 h-auto text-destructive hover:text-destructive hover:bg-destructive/10">
                  <LogOut className="h-5 w-5" />
                  <span className="font-medium">Abmelden</span>
                </Button>
              </div>
            </SheetContent>
          </Sheet>

          <div className="flex items-center gap-2">
            <div className="flex items-center justify-center w-8 h-8 rounded-lg bg-primary text-primary-foreground flex-shrink-0">
              <Zap className="h-5 w-5" />
            </div>
            <span className="font-bold text-foreground">HR-flash</span>
          </div>
        </header>

        {/* Spacer for fixed header */}
        <div className="h-14" />
      </>;
  }

  // Desktop: Standard sidebar
  return <aside className={cn("fixed left-0 top-0 h-screen bg-card border-r border-border flex flex-col transition-all duration-300 z-50", isCollapsed ? "w-16" : "w-64")}>
      {/* Header */}
      <div className={cn("flex flex-col border-b border-border", isCollapsed ? "items-center py-4" : "p-4")}>
        <div className={cn("flex items-center gap-2", isCollapsed && "justify-center")}>
          <div className="flex items-center justify-center w-10 h-10 rounded-lg bg-primary text-primary-foreground flex-shrink-0">
            <Globe className="w-[30px] h-[30px]" />
          </div>
          {!isCollapsed && <div>
              <h1 className="text-lg font-bold text-foreground leading-tight">Flash HR</h1>
              <p className="text-xs text-muted-foreground">Dashboard</p>
            </div>}
        </div>
        <div className={cn("mt-2", isCollapsed && "mt-1")}>
          <VersionDisplay isCollapsed={isCollapsed} />
        </div>
      </div>

      {/* Toggle Button */}
      <Button variant="ghost" size="icon" onClick={toggleSidebar} className="absolute -right-3 top-20 h-6 w-6 rounded-full border border-border bg-card shadow-sm hover:bg-secondary">
        {isCollapsed ? <ChevronRight className="h-3 w-3" /> : <ChevronLeft className="h-3 w-3" />}
      </Button>

      {/* User Info */}
      {profile && !isCollapsed && <div className="px-4 py-3 border-b border-border">
          <p className="text-xs text-muted-foreground">Angemeldet als</p>
          <p className="font-medium text-sm text-foreground truncate">{profile.full_name}</p>
          <p className="text-xs text-primary mt-0.5">
            {userRole === 'admin' && 'Administrator'}
            {userRole === 'hr_manager' && 'Personalmanager'}
            {userRole === 'hr_clerk' && 'Personalverwalter'}
            {userRole === 'employee' && 'Mitarbeiter'}
          </p>
        </div>}

      {/* Navigation */}
      <nav className="flex-1 overflow-y-auto p-2">
        <div className="flex flex-col gap-1">
          {navItems.map(item => <NavLink key={item.path} item={item} />)}
        </div>
      </nav>

      {/* Footer Actions */}
      <div className={cn("p-2 border-t border-border space-y-1", isCollapsed && "flex flex-col items-center")}>
        {isCollapsed ? <>
            <Tooltip delayDuration={0}>
              <TooltipTrigger asChild>
                <Button variant="ghost" size="icon" onClick={toggleTheme} className="h-10 w-10">
                  {isDarkMode ? <Sun className="h-5 w-5" /> : <Moon className="h-5 w-5" />}
                </Button>
              </TooltipTrigger>
              <TooltipContent side="right">
                {isDarkMode ? "Light Mode" : "Dark Mode"}
              </TooltipContent>
            </Tooltip>
            <Tooltip delayDuration={0}>
              <TooltipTrigger asChild>
                <Button variant="ghost" size="icon" onClick={handleSignOut} className="h-10 w-10 text-destructive hover:text-destructive hover:bg-destructive/10">
                  <LogOut className="h-5 w-5" />
                </Button>
              </TooltipTrigger>
              <TooltipContent side="right">
                Abmelden
              </TooltipContent>
            </Tooltip>
          </> : <>
            <Button variant="ghost" onClick={toggleTheme} className="w-full justify-start gap-3 px-3 py-3 h-auto">
              {isDarkMode ? <Sun className="h-5 w-5" /> : <Moon className="h-5 w-5" />}
              <span className="font-medium">{isDarkMode ? "Light Mode" : "Dark Mode"}</span>
            </Button>
            <Button variant="ghost" onClick={handleSignOut} className="w-full justify-start gap-3 px-3 py-3 h-auto text-destructive hover:text-destructive hover:bg-destructive/10">
              <LogOut className="h-5 w-5" />
              <span className="font-medium">Abmelden</span>
            </Button>
          </>}
      </div>
    </aside>;
};
export default Sidebar;